import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import {
  getReportBookmarks,
  createReportBookmark,
  updateReportBookmark,
  deleteReportBookmark,
} from 'api/generated/report-bookmark-controller/report-bookmark-controller';
import type {
  ReportBookmarkDto,
  ReportBookmarkRequest,
} from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';

import toast from 'components/partials/toast/toast';

export const favoriteReportsKeys = {
  all: ['favorite-reports'] as const,
  list: () => [...favoriteReportsKeys.all, 'list'] as const,
};

export interface UseFavoriteReportsResult {
  data: ReportBookmarkDto[] | null;
  loading: boolean;
  error: unknown;
}

export const useFavoriteReports = (): UseFavoriteReportsResult => {
  const currentUser = useCurrentUser();
  const oktaUserId = currentUser.data?.oktaUserId ?? '';
  const userId = currentUser.data?.id;

  const query = useQuery<ReportBookmarkDto[], Error>({
    queryKey: favoriteReportsKeys.list(),
    queryFn: () => getReportBookmarks(oktaUserId, Number(userId)),
    enabled: !!oktaUserId && !!userId,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
};

export const useCreateFavoriteReport = (): UseMutationResult<
  ReportBookmarkDto,
  Error,
  { oktaUserId: string; userId: number; data: ReportBookmarkRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ oktaUserId, userId, data }) =>
      createReportBookmark(oktaUserId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: favoriteReportsKeys.list(),
      });
      toast.success({ title: 'Saved to favorites' });
    },
    onError: () => {
      toast.error({ title: 'Failed to create favorite item' });
    },
  });
};

export const useUpdateFavoriteReport = (): UseMutationResult<
  ReportBookmarkDto,
  Error,
  {
    oktaUserId: string;
    userId: number;
    bookmarkId: number;
    data: ReportBookmarkRequest;
  }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ oktaUserId, userId, bookmarkId, data }) =>
      updateReportBookmark(oktaUserId, userId, bookmarkId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: favoriteReportsKeys.list(),
      });
      toast.success({ title: 'Favorite item updated' });
    },
    onError: () => {
      toast.error({ title: 'Failed to update favorite item' });
    },
  });
};

export const useDeleteFavoriteReport = (): UseMutationResult<
  void,
  Error,
  { oktaUserId: string; userId: number; bookmarkId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ oktaUserId, userId, bookmarkId }) =>
      deleteReportBookmark(oktaUserId, userId, bookmarkId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: favoriteReportsKeys.list(),
      });
      toast.success({ title: 'Removed from favorites' });
    },
    onError: () => {
      toast.error({ title: 'Failed to remove from favorites' });
    },
  });
};
